/**
 * Maps server-side issues to React Hook Form field errors.
 *
 * The API names the offending field `path` (see `reviewService`'s validators,
 * which all emit `{ path, message }`); `field` is accepted too so any older
 * caller shaped that way keeps working. Reading only `field` silently dropped
 * every server-side field error, which is why validation never appeared inline.
 */
export const mapServerIssuesToForm = (issues, setError) => {
  if (!Array.isArray(issues) || !setError) return;

  issues.forEach(issue => {
    const field = issue?.path || issue?.field;
    if (field && issue.message) {
      setError(field, {
        type: "server",
        message: issue.message
      });
    }
  });
};

/**
 * Parses generic API errors into a standard Error object with properties
 * such as status, code, issues, and retryAfter.
 */
import { BANNED_MESSAGE, announceUserBanned } from "./roles";

export const parseApiError = async (response) => {
  let errorData = {};
  
  try {
    errorData = await response.json();
  } catch (e) {
    // If not JSON, leave errorData empty
  }

  const message = errorData.message || errorData.error || `Request failed with status ${response.status}`;
  const err = new Error(message);
  
  err.status = response.status;
  err.code = errorData.code || errorData.error;
  err.issues = errorData.issues || errorData.failed || [];

  // A banned user's action: show the API's own words and tell the app, so the banner shows. Callers never retry this.
  if (err.code === "USER_BANNED") {
    err.message = BANNED_MESSAGE;
    announceUserBanned();
  }
  
  const retryAfterHeader = response.headers.get("Retry-After");
  if (retryAfterHeader) {
    err.retryAfter = parseInt(retryAfterHeader, 10);
  } else if (errorData.retryAfter) {
    err.retryAfter = errorData.retryAfter;
  }
  
  return err;
};
